import {
  UseQueryResult,
  useMutation,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { createGridOverlays } from '../../../../grid-overlay/no-data-overlay-helpers';
import { LabelSync } from '../../shared/types';
import SharedGpanelGrid from '../../shared/components/shared-gpanel-grid';
import GpanelContactsGridToolbar from '../gpanel-contacts/gpanel-contacts-grid/gpanel-contacts-grid-toolbar';
import { useGpanelContactsPermissions } from '../../shared/hooks/use-gpanel-contacts-permissions';
import GpanelContactsSyncDetailsDialog from './gpanel-contacts-sync-details-dialog';
import { getDateTimeString } from '../../shared/utils';
import GPanelContactsDeleteSyncDialog from './gpanel-contacts-delete-sync-dialog';
import { GPanelContactsPagesConfig } from '../../shared/pages-config';
import { useNavigate } from 'react-router-dom';
import {
  restoreSync,
  GPanelContactsKeys,
  getSyncUsersStatus,
} from '../../gpanel-contacts-api';
import { useStore } from '../../../../state/zustand-store';
import TooltipWithPermission from '../../shared/components/tooltip-with-permission';
import { GPanelContactsPermissions } from '../../../../../utils/roles/client-roles';
import { useLabelsQuery } from '../../gpanel-contacts-queries';
import SharedDataGridClient from '../../../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../../../shared/shared-data-grid-toolbar-helper';
import CreateButton from '../../../../../shared/buttons/create-button';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import PeopleAlt from '@mui/icons-material/PeopleAlt';
import Sync from '@mui/icons-material/Sync';
import Cached from '@mui/icons-material/Cached';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Cancel from '@mui/icons-material/Cancel';
import Error from '@mui/icons-material/Error';
import DeleteForever from '@mui/icons-material/DeleteForever';

export default function GpanelContactsSyncPage({
  query,
  title,
  excludeColumns = [],
  showRestoreSyncButton = false,
}: {
  query: UseQueryResult<LabelSync[], Error>;
  title: string;
  excludeColumns?: string[];
  showRestoreSyncButton?: boolean;
}) {
  const { PERMISSIONS } = useGpanelContactsPermissions();
  const [syncForDetailsDialog, setSyncForDetailsDialog] =
    useState<LabelSync | null>(null);
  const [syncForDeletionDialog, setSyncForDeletionDialog] =
    useState<LabelSync | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { data: gpanelLabels = [] } = useLabelsQuery();

  const results = useQueries({
    queries:
      (query.data &&
        query.data.length > 0 &&
        query.data.map((sync) => ({
          queryKey: [GPanelContactsKeys.SyncUsersStatus, sync.id],
          queryFn: () => getSyncUsersStatus(sync.id.toString()),
        }))) ||
      [],
  });

  const restoreSyncMutation = useMutation({
    mutationFn: (syncId: number) => restoreSync(syncId),
    onSuccess: (_restoredSync, syncId) => {
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const [firstKey] = query.queryKey;
            return firstKey === GPanelContactsKeys.LabelSyncs;
          },
        },
        (oldData: LabelSync[]) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;

          const updatedData = oldData.map((sync: LabelSync) => {
            if (sync.id === syncId) {
              return {
                ...sync,
                deleted: false,
                status: 'COMPLETED', // Set to COMPLETED to show green icon immediately
              };
            }
            return sync;
          });

          return updatedData;
        }
      );

      // Show success notification
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Sync restored successfully',
      });
    },
    onError: () => {
      // Show error notification
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to restore sync. Please try again.',
      });
    },
  });

  const headerActions = (
    <Stack flexDirection="row" alignItems="right" gap={1}>
      <TooltipWithPermission
        title=""
        hasPermission={PERMISSIONS.createContactsSync}
        permissionName={GPanelContactsPermissions.CreateContactSync.name}
        renderElement={(hasPermission) => (
          <CreateButton
            disabled={!hasPermission}
            variant="contained"
            onClick={() =>
              navigate(`${GPanelContactsPagesConfig.NewContactSync.link}`)
            }
            label="Create Contact Sync"
          />
        )}
      />
    </Stack>
  );

  const columnDefs = useMemo<GridColDef<LabelSync>[]>(() => {
    const baseColumns: GridColDef<LabelSync>[] = [
      {
        field: 'createdTime',
        headerName: 'Created Date',
        flex: 1,
        valueGetter: (value: number) => {
          const date = new Date(value);
          if (value === 0) {
            return null;
          } else {
            return date;
          }
        },
        valueFormatter: (params: Date) => {
          return getDateTimeString(params);
        },
      },
      {
        field: 'name',
        headerName: 'Sync Name',
        flex: 1,
        valueGetter: (_value, row) => {
          return row.name ?? '';
        },
      },
      {
        field: 'targetLabelName',
        headerName: 'Google Label Name',
        flex: 1,
        valueGetter: (_value, row) => {
          return row.targetLabelName ?? '';
        },
      },
      {
        field: 'gpanelLabel',
        headerName: 'gPanel Label',
        flex: 1,
        valueGetter: (_value, row) => {
          const label = gpanelLabels.find((l) => l.id === row.labelId);
          return label?.name ?? '';
        },
        renderCell: ({ value }) =>
          value ? <Chip label={value} size="small" /> : null,
      },
      {
        field: 'targets',
        headerName: 'Targets',
        flex: 1,
        valueGetter: (_value, row) =>
          row?.allSource ? 'All' : row?.targets.join(', ') ?? '',
        renderCell: ({ value }) => (
          <Stack
            flexDirection="row"
            gap={1}
            sx={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
          >
            {value}
          </Stack>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        valueGetter: (_value, row) => row?.status ?? 'Status Unavailable',
        renderCell: ({ value, row }) => {
          // Use sync users status query to check for errors
          const syncUsersStatusQuery = results.find(
            (result) =>
              result.data &&
              result.data.length > 0 &&
              result.data[0].syncId === row.id
          );

          // Function to derive actual status based on sync status and user errors
          const getDerivedStatus = (syncStatus: string, usersStatus: any[]) => {
            // If sync is completed but has user errors, return COMPLETE_WITH_ERRORS
            if (
              syncStatus?.toUpperCase() === 'COMPLETED' &&
              usersStatus &&
              usersStatus.length > 0
            ) {
              const hasErrors = usersStatus.some(
                (user) => user.error && user.error.trim() !== ''
              );

              if (hasErrors) {
                return 'COMPLETE_WITH_ERRORS';
              }
            }

            return syncStatus;
          };

          const actualStatus = getDerivedStatus(
            value,
            syncUsersStatusQuery?.data || []
          );

          const getStatusDisplay = (status: string) => {
            switch (status?.toUpperCase()) {
              case 'PENDING':
                return {
                  label: 'In Progress',
                  color: 'info' as const,
                  icon: <Cached />,
                };
              case 'COMPLETED':
                return {
                  label: 'Active',
                  color: 'success' as const,
                  icon: <CheckCircleOutline />,
                };
              case 'FAILED':
                return {
                  label: 'Failed',
                  color: 'error' as const,
                  icon: <Cancel />,
                };
              case 'DELETING':
                return {
                  label: 'Deleting',
                  color: 'error' as const,
                  icon: <DeleteForever />,
                };
              case 'DISCONNECTED':
                return {
                  label: 'Disconnected',
                  color: 'default' as const,
                  icon: <Cached />,
                };
              case 'COMPLETE_WITH_ERRORS': {
                const errorCount = (syncUsersStatusQuery?.data || []).filter(
                  (user) => user.error && user.error.trim() !== ''
                ).length;
                return {
                  label: `Complete with Errors (${errorCount})`,
                  color: 'warning' as const,
                  icon: <Error />,
                };
              }
              default:
                return {
                  label: status ?? 'Status Unavailable',
                  color: 'default' as const,
                  icon: null,
                };
            }
          };

          const statusDisplay = getStatusDisplay(actualStatus);
          const isClickable =
            actualStatus?.toUpperCase().includes('ERRORS') ||
            actualStatus?.toUpperCase() === 'FAILED';

          const chipComponent = (
            <Chip
              label={statusDisplay.label}
              size="small"
              variant="outlined"
              color={statusDisplay.color}
              title={statusDisplay.label}
              {...(statusDisplay.icon && { icon: statusDisplay.icon })}
              sx={{
                '&:hover': isClickable
                  ? {
                      cursor: 'pointer',
                      backgroundColor: (theme) => theme.palette.warning.light,
                      opacity: 0.8,
                    }
                  : {},
              }}
            />
          );

          if (isClickable) {
            return (
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(
                    `${row.id}/${GPanelContactsPagesConfig.UserStatusContactSync.link}`
                  );
                }}
                style={{ cursor: 'pointer' }}
              >
                {chipComponent}
              </div>
            );
          }

          return chipComponent;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (row) => [
          <GridActionsCellItem
            key="edit-sync"
            label="Edit Sync"
            icon={<Edit />}
            disabled={!PERMISSIONS.editContactsSync}
            onClick={() =>
              navigate(
                `${row.id}/${GPanelContactsPagesConfig.EditContactSync.link}`
              )
            }
            showInMenu
          />,
          <GridActionsCellItem
            key="delete-sync"
            label="Delete Sync"
            icon={<Delete />}
            disabled={!PERMISSIONS.deleteContactsSync}
            onClick={() => setSyncForDeletionDialog(row.row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="view-user-status"
            label="View User Status"
            icon={<PeopleAlt />}
            onClick={() =>
              navigate(
                `${row.id}/${GPanelContactsPagesConfig.UserStatusContactSync.link}`
              )
            }
            showInMenu
          />,
        ],
      },
    ];

    // Conditionally add Restore Sync column before actions
    if (showRestoreSyncButton) {
      baseColumns.splice(baseColumns.length - 1, 0, {
        field: 'Restore Sync',
        headerName: '',
        flex: 1,
        renderCell: (params) => (
          <Button
            variant="contained"
            size="small"
            onClick={(event) => {
              event.stopPropagation(); // Prevent row click event
              restoreSyncMutation.mutate(params.row.id);
            }}
            disabled={restoreSyncMutation.isPending}
          >
            {restoreSyncMutation.isPending ? 'RESTORING...' : 'RESTORE SYNC'}
          </Button>
        ),
      });
    }

    return baseColumns;
  }, [
    PERMISSIONS,
    showRestoreSyncButton,
    restoreSyncMutation.isPending,
    gpanelLabels,
  ]);

  const columns = columnDefs.filter(
    (column) => !excludeColumns.includes(column.field)
  );

  const toolbarProps = useMemo(
    () => ({
      actions: headerActions,
      isLoading: query.isFetching,
      onRefresh: () => query.refetch(),
      title,
      selection: [],
    }),
    [headerActions, query, title]
  );

  return (
    <>
      <SharedGpanelGrid>
        <SharedDataGridClient
          columns={columns}
          rows={query.data || []}
          loading={query.isFetching}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          pagination
          pageSizeOptions={[25, 50, 100]}
          defaultGridState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          slots={{
            toolbar: useToolbar(GpanelContactsGridToolbar, toolbarProps),
            ...createGridOverlays(
              'Syncs',
              <Sync />,
              `There may be no Syncs in the ${title}.`,
              'No Syncs Found'
            ),
          }}
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': {
              alignContent: 'center',
              alignItems: 'center',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
            '& .MuiDataGrid-row': {
              alignItems: 'stretch',
              cursor: 'pointer',
            },
            '&.MuiDataGrid-root': { border: 'none' },
            '&.MuiDataGrid-root .MuiDataGrid-container--top [role=row]': {
              backgroundColor: (theme) => theme.palette.background.paper,
            },
          }}
          onRowClick={(row) => setSyncForDetailsDialog(row.row)}
        />
      </SharedGpanelGrid>
      {syncForDetailsDialog && (
        <GpanelContactsSyncDetailsDialog
          sync={syncForDetailsDialog}
          handleClose={() => setSyncForDetailsDialog(null)}
        />
      )}
      {syncForDeletionDialog && (
        <GPanelContactsDeleteSyncDialog
          sync={syncForDeletionDialog}
          handleClose={() => setSyncForDeletionDialog(null)}
        />
      )}
    </>
  );
}
